import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef } from "react";
import { Animated, Pressable, StyleSheet, Text, View } from "react-native";

import { colors, fonts, spacing, radius, shadows, gradients } from "@/lib/theme";
import type { VisitUIState } from "@/lib/visit-state";

interface StickyBottomButtonProps {
    visitState: VisitUIState;
    onClockIn: () => void;
    onClockOut: () => void;
    onViewPayment: () => void;
    disabled?: boolean;
    /** Clocked out already, paperwork still open: the action is "Submit" only. */
    alreadyClockedOut?: boolean;
}

export function StickyBottomButton({
    visitState,
    onClockIn,
    onClockOut,
    onViewPayment,
    disabled,
    alreadyClockedOut
}: StickyBottomButtonProps) {
    const pulseAnim = useRef(new Animated.Value(1)).current;

    // Pulse animation for State 3 (submitted, clock out)
    useEffect(() => {
        if (visitState === 3) {
            const animation = Animated.loop(
                Animated.sequence([
                    Animated.timing(pulseAnim, {
                        toValue: 1.03,
                        duration: 800,
                        useNativeDriver: true
                    }),
                    Animated.timing(pulseAnim, {
                        toValue: 1,
                        duration: 800,
                        useNativeDriver: true
                    })
                ])
            );
            animation.start();
            return () => animation.stop();
        }
        pulseAnim.setValue(1);
    }, [visitState, pulseAnim]);

    if (visitState === 1) {
        // CLOCK IN — teal gradient
        return (
            <View style={styles.container}>
                <Pressable
                    onPress={onClockIn}
                    disabled={disabled}
                    style={({ pressed }) => [pressed && styles.pressed]}
                >
                    <LinearGradient
                        colors={gradients.teal}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={[styles.button, shadows.teal]}
                    >
                        <Text style={styles.buttonTextWhite}>CLOCK IN</Text>
                    </LinearGradient>
                </Pressable>
            </View>
        );
    }

    if (visitState === 2) {
        // SUBMIT & CLOCK OUT — the terminal action during an active visit.
        // Submits the assessment if complete, otherwise clocks out with a
        // required note. Secondary style.
        return (
            <View style={styles.container}>
                <Pressable
                    onPress={onClockOut}
                    disabled={disabled}
                    style={({ pressed }) => [styles.buttonSecondary, pressed && styles.pressed]}
                >
                    <Text style={styles.buttonTextTeal}>
                        {alreadyClockedOut ? "SUBMIT" : "SUBMIT & CLOCK OUT"}
                    </Text>
                </Pressable>
            </View>
        );
    }

    if (visitState === 3) {
        // CLOCK OUT — already submitted (legacy / recovery: submitted but not
        // yet clocked out). Secondary with pulse animation.
        return (
            <View style={styles.container}>
                <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
                    <Pressable
                        onPress={onClockOut}
                        disabled={disabled}
                        style={({ pressed }) => [
                            styles.buttonSecondary,
                            styles.buttonPulse,
                            pressed && styles.pressed
                        ]}
                    >
                        <Text style={styles.buttonTextTeal}>CLOCK OUT</Text>
                    </Pressable>
                </Animated.View>
            </View>
        );
    }

    // State 4 — VIEW MY PAYMENT — green gradient
    return (
        <View style={styles.container}>
            <Pressable
                onPress={onViewPayment}
                disabled={disabled}
                style={({ pressed }) => [pressed && styles.pressed]}
            >
                <LinearGradient
                    colors={gradients.success}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.button, shadows.sm]}
                >
                    <Text style={styles.buttonTextWhite}>VIEW MY PAYMENT</Text>
                </LinearGradient>
            </Pressable>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        paddingHorizontal: spacing.base,
        paddingBottom: spacing.xl,
        paddingTop: spacing.md,
        backgroundColor: colors.background
    },
    button: {
        height: 56,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center"
    },
    buttonSecondary: {
        height: 56,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.teal400
    },
    buttonPulse: {
        borderColor: colors.teal400,
        borderWidth: 2
    },
    buttonTextWhite: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: "#FFFFFF",
        letterSpacing: 0.5
    },
    buttonTextTeal: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.teal600,
        letterSpacing: 0.5
    },
    pressed: {
        opacity: 0.9,
        transform: [{ scale: 0.98 }]
    }
});
